import { useEffect, useMemo } from 'react';
import { useHomeData } from '../contexts/HomeDataContext';
import { HeroSection } from '../components/HeroSection';
import { CitiesSection } from '../components/CitiesSection';
import { ExperienceCarousel } from '../components/ExperienceCarousel';
import { ActiveFiltersBar } from '../components/ActiveFiltersBar';
import { ExperienceCard } from '../components/ExperienceCard';
import { FilterRail } from '../components/FilterRail';
import { TrustStrip, BookingTypeLegend } from '../components/TrustStrip';
import type {
  ExperienceListItem,
  HomeCategory,
  HomeExperienceCard,
} from '../types';

const norm = (s: string) => s.toLowerCase().trim();

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function SkeletonCarouselItem() {
  return (
    <div className="carousel__item">
      <article className="card exp-card">
        <div className="card-media">
          <div className="skeleton skeleton--media" />
        </div>
        <div className="card-body">
          <div className="skeleton skeleton--line skeleton--line-xs" style={{ width: '30%' }} />
          <div className="skeleton skeleton--title" />
          <div className="skeleton skeleton--line skeleton--line-sm" />
        </div>
      </article>
    </div>
  );
}

function LoadingHome() {
  return (
    <main className="home">
      <HeroSection
        onExplore={() => undefined}
        onJumpCities={() => undefined}
        onJumpTrending={() => undefined}
        onJumpPremium={() => undefined}
        onJumpCouple={() => undefined}
      />
      <div className="home__content">
        <section className="section">
          <div className="section-head">
            <div>
              <div className="skeleton skeleton--title" />
              <div className="skeleton skeleton--line skeleton--line-sm" />
            </div>
          </div>
          <div className="carousel" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <SkeletonCarouselItem key={i} />
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

function groupTagsByCategory(activeTags: string[], categories: HomeCategory[]): Map<string, Set<string>> {
  const catIndex = new Map<string, string>(categories.map((c) => [norm(c.slug), c.group]));
  const map = new Map<string, Set<string>>();
  for (const slug of activeTags) {
    const key = norm(slug);
    const group = catIndex.get(key);
    if (!group) continue;
    let bucket = map.get(group);
    if (!bucket) {
      bucket = new Set<string>();
      map.set(group, bucket);
    }
    bucket.add(key);
  }
  return map;
}

function matchesCardFilters(
  card: { citySlugs?: string[] | null; citySlug?: string | null; tagSlugs?: string[] | null },
  citySlug: string | null,
  activeTagsByGroup: Map<string, Set<string>>,
): boolean {
  if (citySlug) {
    const target = norm(citySlug);
    const haystack = new Set<string>();
    for (const s of card.citySlugs ?? []) haystack.add(norm(s));
    if (card.citySlug) haystack.add(norm(card.citySlug));
    if (!haystack.has(target)) return false;
  }
  if (activeTagsByGroup.size === 0) return true;
  const expTagSet = new Set((card.tagSlugs ?? []).map(norm));
  for (const [, selected] of activeTagsByGroup) {
    let hit = false;
    for (const s of selected) if (expTagSet.has(s)) { hit = true; break; }
    if (!hit) return false;
  }
  return true;
}

function matchesListItemFilters(
  exp: ExperienceListItem,
  citySlug: string | null,
  activeTagsByGroup: Map<string, Set<string>>,
): boolean {
  if (citySlug) {
    const expCities = (exp.citySlugs ?? []).map(norm);
    if (!expCities.includes(norm(citySlug))) return false;
  }
  if (activeTagsByGroup.size === 0) return true;
  const expTagSet = new Set((exp.tagSlugs ?? []).map(norm));
  for (const [, selected] of activeTagsByGroup) {
    let hit = false;
    for (const s of selected) if (expTagSet.has(s)) { hit = true; break; }
    if (!hit) return false;
  }
  return true;
}

export function HomePage() {
  const {
    home: homeData,
    homeLoading,
    homeError,
    allExperiences,
    filters,
    setCity,
    toggleTag,
    removeTag,
    clearAll,
  } = useHomeData();

  const hasFilter = Boolean(filters.city) || filters.tags.length > 0;

  const tagsByGroup = useMemo(
    () => (homeData ? groupTagsByCategory(filters.tags, homeData.categories) : new Map()),
    [homeData, filters.tags],
  );

  const filteredHome = useMemo(() => {
    if (!homeData) return null;
    const apply = (items: HomeExperienceCard[]) =>
      items.filter((c) => matchesCardFilters(c, filters.city, tagsByGroup));
    return {
      trending: apply(homeData.trending),
      featured: apply(homeData.featured),
      premium: apply(homeData.premium),
      couple: apply(homeData.couple),
    };
  }, [homeData, filters.city, tagsByGroup]);

  const filteredCatalog = useMemo(() => {
    if (!hasFilter || !allExperiences) return null;
    return allExperiences.filter((e) => matchesListItemFilters(e, filters.city, tagsByGroup));
  }, [hasFilter, allExperiences, filters.city, tagsByGroup]);

  useEffect(() => {
    if (!homeData || !allExperiences) return;
    /* eslint-disable no-console */
    console.log('[HOME] sizes', {
      trending: homeData.trending.length, featured: homeData.featured.length,
      premium: homeData.premium.length, couple: homeData.couple.length,
      cities: homeData.cities.length, categories: homeData.categories.length,
      fullCatalog: allExperiences.length,
    });
    /* eslint-enable no-console */
  }, [homeData, allExperiences]);

  if (homeLoading) return <LoadingHome />;

  if (homeError || !homeData || !filteredHome) {
    return (
      <main className="home">
        <HeroSection
          onExplore={() => undefined} onJumpCities={() => undefined}
          onJumpTrending={() => undefined} onJumpPremium={() => undefined}
          onJumpCouple={() => undefined}
        />
        <div className="home__content">
          {homeError && (
            <div className="error-box">
              <h3>Não conseguimos carregar a página inicial</h3>
              <p>Tente novamente em instantes.</p>
              <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
                Tentar novamente
              </button>
            </div>
          )}
        </div>
      </main>
    );
  }

  const resultsCount = filteredCatalog?.length ?? 0;
  const cityLabel = filters.city
    ? homeData.cities.find((c) => norm(c.slug) === norm(filters.city!))?.name ?? filters.city
    : null;
  const tagChips = filters.tags.map((slug) => {
    const cat = homeData.categories.find((c) => norm(c.slug) === norm(slug));
    return { slug, label: cat?.label ?? slug };
  });

  const handleSelectCity = (slug: string) => {
    setCity(norm(filters.city ?? '') === norm(slug) ? null : slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="home">
      {!hasFilter && (
        <HeroSection
          onExplore={() => scrollToId('trending')}
          onJumpCities={() => scrollToId('cities')}
          onJumpTrending={() => scrollToId('trending')}
          onJumpPremium={() => scrollToId('premium')}
          onJumpCouple={() => scrollToId('couple')}
        />
      )}

      <div className="home__content">
        {!hasFilter && <TrustStrip />}
        {!hasFilter && <BookingTypeLegend />}

        <ActiveFiltersBar
          cityLabel={cityLabel}
          tagChips={tagChips}
          onClearCity={() => setCity(null)}
          onRemoveTag={removeTag}
          onClearAll={clearAll}
          resultsCount={resultsCount}
        />

        {!hasFilter && (
          <CitiesSection
            cities={homeData.cities}
            activeSlug={filters.city}
            onSelect={handleSelectCity}
          />
        )}

        <FilterRail
          categories={homeData.categories}
          activeTags={filters.tags}
          onToggle={toggleTag}
          onClearAll={clearAll}
        />

        {hasFilter ? (
          <section className="section" id="results">
            <div className="section-head">
              <div>
                <h2>Resultados</h2>
                <p>{resultsCount} experiência{resultsCount === 1 ? '' : 's'} encontrada{resultsCount === 1 ? '' : 's'}</p>
              </div>
            </div>
            {filteredCatalog === null ? (
              <p className="catalog-loading">Carregando catálogo...</p>
            ) : filteredCatalog.length === 0 ? (
              <div className="empty-results">
                <h3>Nenhuma experiência encontrada</h3>
                <p>Remova alguns filtros ou combine de outra forma. O catálogo está aumentando.</p>
                <button type="button" className="btn btn--primary" onClick={clearAll}>Limpar filtros</button>
              </div>
            ) : (
              <div className="grid">
                {filteredCatalog.map((exp) => <ExperienceCard key={exp.experienceCode} exp={exp} />)}
              </div>
            )}
          </section>
        ) : (
          <>
            <div id="trending">
              <ExperienceCarousel
                title={<>Em <span className="title-serif">Alta</span></>}
                description="As mais reservadas nos últimos dias."
                items={filteredHome.trending}
              />
            </div>
            <ExperienceCarousel
              title={<>Experiências em <span className="title-serif">Destaque</span></>}
              description="Selecionadas por curadoria para aproveitar hoje."
              items={filteredHome.featured}
            />
            <div id="premium">
              <ExperienceCarousel
                title={<>Experiências <span className="title-serif">Premium</span></>}
                description="Para ocasiões especiais e quem busca algo único."
                items={filteredHome.premium}
              />
            </div>
            <div id="couple">
              <ExperienceCarousel
                title={<>Para Curtir <span className="title-serif">a Dois</span></>}
                description="Roteiros pensados para casais."
                items={filteredHome.couple}
              />
            </div>
          </>
        )}
      </div>
    </main>
  );
}
